import { useMemo } from 'react';
import { colorVariant, sizeVariant, ProductVariantItem, freeProduct } from 'lib/productsState/productsTypes';

export const calculateDefaultVariants = (
  colorVariants: colorVariant[] | null,
  sizeVariants: sizeVariant[] | null,
  enableColorVariants: boolean,
  enableSizeVariants: boolean
): ProductVariantItem | null => {
  let defaultVariant: ProductVariantItem | null = null;

  if (enableColorVariants && colorVariants && colorVariants.length > 0) {
    const defaultColorVariant = colorVariants.find((variant) => variant.default_variant === true) || colorVariants[0];

    if (defaultColorVariant) {
      defaultVariant = {
        color: defaultColorVariant.sku,
        colorLabel: defaultColorVariant.name,
        color_hex: defaultColorVariant.color_hex,
      };
    }
  }

  if (enableSizeVariants && sizeVariants && sizeVariants.length > 0) {
    const defaultSizeVariant = sizeVariants.find((variant) => variant.default_variant === true) || sizeVariants[0];

    if (defaultSizeVariant) {
      defaultVariant = {
        ...defaultVariant,
        size: defaultSizeVariant.sku,
        sizeLabel: defaultSizeVariant.name,
      };
    }
  }

  if (defaultVariant?.color || defaultVariant?.size) {
    defaultVariant.variantId = `variant_${defaultVariant.color}_${defaultVariant.size}`;
  }

  return defaultVariant;
};

export const useDefaultVariants = (
  colorVariants: colorVariant[] | null,
  sizeVariants: sizeVariant[] | null,
  enableColorVariants: boolean,
  enableSizeVariants: boolean
): ProductVariantItem => {
  return useMemo(
    () => calculateDefaultVariants(colorVariants, sizeVariants, enableColorVariants, enableSizeVariants),
    [colorVariants, sizeVariants, enableColorVariants, enableSizeVariants]
  );
};

export const getSanitizedVariantsForItem = (
  sizeVariants: sizeVariant[],
  colorVariants: colorVariant[],
  currentVariant: ProductVariantItem,
  enableColorVariants: boolean
): productVariant[] => {
  if (!sizeVariants) {
    return [];
  }

  if (!enableColorVariants || !colorVariants || !currentVariant) {
    return sizeVariants;
  }

  const itemColor = currentVariant.color || '';

  if (!itemColor) {
    return sizeVariants;
  }

  const currentColorVariant = colorVariants.find(
    (colorVariant: colorVariant) => colorVariant.sku?.toLowerCase() === itemColor?.toLowerCase()
  );

  if (!currentColorVariant || !currentColorVariant.blacklisted_sizes) {
    return sizeVariants;
  }

  const blacklistedSizes = currentColorVariant.blacklisted_sizes.split(',').map((size) => size.trim().toLowerCase());

  const filteredVariants = sizeVariants.filter((sizeVariant: sizeVariant) => {
    const sizeName = sizeVariant.sku?.toLowerCase() || '';
    const isBlacklisted = blacklistedSizes.includes(sizeName);
    return !isBlacklisted;
  });

  return filteredVariants;
};

export const getItemsAmount = (free_products: freeProduct[], amount: number): number => {
  const freeProductsAmount = free_products.reduce(
    (acc, item) => acc + (item.apply_variants ? Number(item.fp_amount) : 0),
    0
  );

  return freeProductsAmount + amount;
};
